import {createAction} from '@reduxjs/toolkit'

import {RouterContextProps} from '../../_web/router/context/RouterContext'
import {
  AuthenticationFlows,
  AuthenticationMethod,
  SourceComponentType
} from '../types/Auth'
import {User} from '../types/Cognito'

/**
 * A timeout that gives the analytics systems to send the request before the application
 * sends the user away.
 */
export const TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY = 50

/**
 * Fires an action to tracks that the user clicked on authentication buttons inside the overlay
 *
 * `sourceComponent` and `authenticationFlow` are nullable, not optional: omitting
 * them used to compile, which is how entry points ended up reporting `null`.
 */
export const trackUserAuthClick = createAction(
  'authentication/trackUserAuthClick',
  ({
    sourceComponent,
    authenticationMethod,
    analyticsData,
    authenticationFlow
  }: {
    sourceComponent: string | null
    authenticationMethod: AuthenticationMethod
    analyticsData?: Record<string, unknown>
    authenticationFlow: AuthenticationFlows | null
  }) => ({
    payload: {
      sourceComponent,
      authenticationMethod,
      analyticsData,
      authenticationFlow
    }
  })
)

/**
 * Fires an action after succesfull user authentication with email
 */
export const userSuccessfullyAuthenticatedWithEmail = createAction(
  'authentication/userSuccessfullyAuthenticatedWithEmail',
  () => ({
    payload: {}
  })
)

/**
 * Fires an action after succesfull user authentication
 */
export const userSuccessfullyAuthenticated = createAction(
  'authentication/userSuccessfullyAuthenticated',
  ({
    replace,
    user
  }: {
    replace?: RouterContextProps['replace']
    user?: User
  }) => ({
    payload: {
      replace,
      user
    }
  })
)

/**
 * Fires an action to tracks places where user did not authenticate successfully
 */
export const userDidNotAuthenticateSuccessfully = createAction(
  'authentication/userDidNotAuthenticateSuccessfully',
  ({replace}: {replace?: RouterContextProps['replace']}) => ({
    payload: {
      replace
    }
  })
)

/**
 * Fires an action to tracks places where user open the auth overlay with a SignUp button
 */
export const trackSignUpClick = createAction<SourceComponentType>(
  'authentication/trackSignUpClick'
)

/**
 * Fires an action to tracks places where user open the auth overlay with a Login button
 */
export const trackLoginClick = createAction<SourceComponentType>(
  'authentication/trackLoginClick'
)

/**
 * Fires an action that tells the application that the user information is ready
 * for identification on our systems
 * @param user - The user {@link User}
 */
export const identifyAuthenticatedUser = createAction<User>(
  'authentication/identifyAuthenticatedUser'
)

/**
 * Fires an action that tells the application that the user has just authenticated
 */
export const userHasJustAuthenticated = createAction(
  'authentication/userHasJustAuthenticated'
)
